import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FileStack,
  BarChart3,
  Settings as SettingsIcon,
  UploadCloud,
  LogOut,
  Users,
  Building2,
  User,
  ShieldCheck,
  Menu,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const NAV = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/app/templates", label: "Templates", icon: FileStack, testid: "nav-templates" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function NavList({ onNavigate }) {
  return (
    <nav className="px-3 flex-1 space-y-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testid}
          onClick={onNavigate}
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserMenu({ user, initials, onLogout, onNavigate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <div className="h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">
            {initials}
          </div>
          <div className="flex-1 text-left overflow-hidden">
            <div className="text-sm font-medium text-slate-900 truncate">
              {user?.name}
            </div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel>
          <div className="text-xs uppercase tracking-wider text-slate-500">Signed in as</div>
          <div className="text-sm font-medium text-slate-900 truncate">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onNavigate("/app/profile")} data-testid="menu-profile">
          <User className="h-4 w-4 mr-2" /> Your profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate("/app/security")} data-testid="menu-security">
          <ShieldCheck className="h-4 w-4 mr-2" /> Security
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate("/app/settings")}>
          <Building2 className="h-4 w-4 mr-2" /> Organization
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          data-testid="logout-btn"
          className="text-red-600 focus:text-red-600"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const goAndClose = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-[100dvh] flex bg-slate-50 text-slate-900">
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-64 border-r border-slate-200 bg-white hidden md:flex flex-col sticky top-0 h-screen"
      >
        <div className="px-5 py-5 border-b border-slate-200 flex items-center">
          <Link to="/app" data-testid="brand-name" className="inline-flex items-center">
            <Logo className="h-7" />
          </Link>
        </div>

        <div className="px-4 py-4">
          <Button
            onClick={() => navigate("/app/documents/upload")}
            data-testid="sidebar-upload-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm gap-2"
          >
            <UploadCloud className="h-4 w-4" /> Upload document
          </Button>
        </div>

        <NavList />

        <div className="p-4 border-t border-slate-200">
          <UserMenu user={user} initials={initials} onLogout={handleLogout} onNavigate={navigate} />
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-3 py-2.5 flex items-center justify-between gap-2 pt-[max(env(safe-area-inset-top),0.5rem)]">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-trigger"
              className="p-2 -ml-2 rounded-md hover:bg-slate-100 active:bg-slate-200"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-drawer">
            <SheetHeader className="px-5 py-4 border-b border-slate-200 text-left">
              <SheetTitle asChild>
                <Link to="/app" onClick={() => setMobileOpen(false)} className="inline-flex">
                  <Logo className="h-7" />
                </Link>
              </SheetTitle>
            </SheetHeader>

            <div className="px-4 py-4">
              <Button
                onClick={() => goAndClose("/app/documents/upload")}
                data-testid="mobile-upload-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm gap-2"
              >
                <UploadCloud className="h-4 w-4" /> Upload document
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto pb-2">
              <NavList onNavigate={() => setMobileOpen(false)} />
            </div>

            <div className="p-4 border-t border-slate-200">
              <UserMenu user={user} initials={initials} onLogout={handleLogout} onNavigate={goAndClose} />
            </div>
          </SheetContent>
        </Sheet>

        <Link to="/app" className="flex items-center" aria-label="Attestra home">
          <Logo className="h-6" />
        </Link>

        <div className="flex items-center gap-1">
          <NotificationBell />
          <button
            onClick={() => navigate("/app/documents/upload")}
            data-testid="mobile-upload-quick"
            className="p-2 rounded-md bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white"
            aria-label="Upload document"
          >
            <UploadCloud className="h-4 w-4" />
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 mt-[56px] md:mt-0 pb-[env(safe-area-inset-bottom)]">
        {/* Desktop top bar with bell */}
        <div className="hidden md:flex items-center justify-end gap-1 px-6 h-12 border-b border-slate-200 bg-white sticky top-0 z-30">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
